import { Component, Inject } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef
} from '@angular/material/legacy-dialog';
import { CtButtonConfiguration, MAT_RAISED_PRIMARY } from '@ctsolution/ct-framework';
import { ItemHandling } from '../../../../_core/classes/item-handling';
import { HandlingStateEnum } from '../../../../_core/enum/handling-state.enum';
import { CTConfigurationModel } from 'projects/ct-manager/src/app/_core/classes/ct-configuration-model';
/** Colonne disponibili nella dialog di dettaglio articoli. */
export type HandlingItemsDialogColumn = 'code' | 'name' | 'description' | 'price' | 'qty' | 'additionalCode';

/** Elenco completo delle colonne nell'ordine di visualizzazione canonico. */
const ALL_COLUMNS: HandlingItemsDialogColumn[] = ['code', 'name', 'description', 'additionalCode', 'price', 'qty'];

/**
 * Dati passati alla modale tramite MAT_DIALOG_DATA.
 */
export interface HandlingItemsDialogData {
  /** Numero identificativo dell'ordine (usato nel titolo della modale) */
  handlingNumber: number | null;
  /** Lista degli ItemHandling da mostrare nella tabella */
  itemHandlings: ItemHandling[];
  /**
   * Stato dell'handling: determina se usare Qty (Delivered) o StartQty per il calcolo del totale.
   * Replica la logica BE: Price * (Delivered ? Qty : StartQty)
   */
  handlingState: HandlingStateEnum | null;
  /**
   * Colonne da mostrare. Array vuoto o non valorizzato = tutte le colonne.
   * Rispetta l'ordine di ALL_COLUMNS indipendentemente dall'ordine passato.
   */
  visibleColumns?: string[];
}

/**
 * Modale di dettaglio articoli di un Ordine (Handling).
 * Visualizza una tabella Material semplice con i campi:
 * Code, Name, Description, Price, Qty dell'Item.
 *
 * Aperta tramite CtModelOperator.Detail dal handling-list.component.
 */
@Component({
  selector: 'app-handling-items-dialog',
  templateUrl: './handling-items-dialog.component.html',
  styleUrls: ['./handling-items-dialog.component.scss']
})
export class HandlingItemsDialogComponent {

  /** Colonne effettivamente visualizzate: calcolate da visibleColumns o di default tutte */
  readonly displayedColumns: string[];

  /** Pulsante di chiusura modale */
  closeButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel('close')
    .setAction(() => this.close())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  /**
   * Calcola il totale di riga replicando esattamente la logica BE:
   * Price * (HandlingState == Delivered ? Qty : StartQty)
   *
   * @param row - La riga ItemHandling
   * @returns Il valore calcolato, oppure null se Price è assente
   */
  getItemTotal(row: ItemHandling): number | null {

    if (row.Price == null) return null;

    // Se lo stato è Delivered si usa Qty, altrimenti StartQty (logica identica al BE)
    const qty = this.data.handlingState === HandlingStateEnum.Delivered
      ? (row.Qty ?? 0)
      : (row.StartQty ?? 0);

    return row.Price * qty;

  }

  constructor(
    private dialogRef: MatDialogRef<HandlingItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HandlingItemsDialogData
  ) {
    // Calcola le colonne da mostrare:
    // - Se visibleColumns è vuoto o non valorizzato → mostra tutte (backward compatible)
    // - Altrimenti filtra ALL_COLUMNS mantenendo l'ordine canonico
    const requested = data.visibleColumns?.length ? data.visibleColumns : [];
    this.displayedColumns = requested.length
      ? ALL_COLUMNS.filter(col => requested.includes(col))
      : [...ALL_COLUMNS];
  }

  /** Chiude la modale */
  close(): void {
    this.dialogRef.close();
  }

}
